import { useState } from "react";
import { Play, ShareAndroid } from "iconoir-react";
import type { Puzzle } from "../../shared/nonogram/types";
import { dailyDatePath } from "../../shared/seo/dailyDate";
import { DIFFICULTY_NAV } from "../../shared/seo/navigation";
import { SOLVE_TIERS } from "../../shared/nonogram/solveTier";
import { useGame } from "../game/useGame";
import { track } from "../lib/analytics";
import { loadBoardZoom, saveBoardZoom } from "../lib/storage";
import { GameBoard } from "./GameBoard";
import { GameControls } from "./GameControls";
import { ResetButton } from "./ResetButton";
import { TrustBadge } from "./TrustBadge";

type Props = {
  puzzle: Puzzle;
  /** The page's own h1, from its SEO definition, so the app and the pre-rendered
      document never disagree about what this page is called. */
  heading?: string;
  mode: "daily" | "standard";
  dailyDate?: string;
  nextPuzzleAt?: string;
  onNewPuzzle?: () => void;
  /** Play it for real without recording it: see `sandbox` in useGame. */
  sandbox?: boolean;
};

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function resultText(puzzle: Puzzle, seconds: number, mistakes: number, streak: number, date?: string): string {
  const heading = date ? `Daily Nonogram · ${date}` : `Nonogram · ${puzzle.difficulty}`;
  // The picture, not the stopwatch, is what makes a shared result worth opening,
  // so the emoji leads. The name stays out of it: it would spoil the same puzzle
  // for whoever reads the message.
  const streakLine = date && streak > 0 ? ` · 🔥 ${streak}-day streak` : "";
  return `${heading}\n${puzzle.emoji} Revealed a ${puzzle.size}×${puzzle.size} picture in ${formatTime(seconds)} · ${mistakes} ${mistakes === 1 ? "mistake" : "mistakes"}${streakLine}\n`;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

export function PuzzleExperience({ puzzle, mode, dailyDate, nextPuzzleAt, onNewPuzzle, heading: pageHeading, sandbox }: Props) {
  const currentPath = window.location.pathname.replace(/\/$/, "") || "/";
  const game = useGame({ puzzle, mode, dailyDate, sandbox, sessionId: dailyDate ? `daily-${dailyDate}` : puzzle.id });
  const [shareStatus, setShareStatus] = useState("");
  // Zoom is a per-size memory: what suits a 5×5 cramps a 25×25, and remembering
  // them together means every switch fights the last choice.
  const [zoom, setZoom] = useState(() => loadBoardZoom(puzzle.size));
  const [pannable, setPannable] = useState(false);
  // Relaxed mode reports every wrong square at once when the player asks, so the
  // single-square wording only holds for Challenge mode's one-at-a-time feedback.
  const firstMistake = game.mistakeCells[0];
  const mistakeMessage = game.mistakeCells.length === 0 || firstMistake === undefined
    ? ""
    : game.mistakeCells.length === 1
      ? `That square is wrong: row ${Math.floor(firstMistake / puzzle.size) + 1}, column ${(firstMistake % puzzle.size) + 1}.`
      : `${game.mistakeCells.length} marked squares do not match the picture.`;
  const statusMessage = mistakeMessage || game.checkMessage || (game.resumed ? "Picked up where you left off." : "");
  const panHint = !pannable
    ? ""
    : game.boardMode === "move"
      ? "Drag to pan sideways or scroll the page."
      : "Use Move or the numbers to pan and scroll.";
  const today = new Date().toISOString().slice(0, 10);
  const dailyHeading = dailyDate === today ? "Today’s Nonogram" : `Daily · ${dailyDate}`;

  // Before the last square goes in the heading must not hint at the subject; the
  // name and emoji are held back so finishing the grid is what reveals them.
  const eyebrow = game.completed
    ? "Picture revealed"
    : mode === "daily"
      ? (dailyDate === today ? dailyDate : "From the archive")
      : puzzle.size >= 20
        ? `${puzzle.size}×${puzzle.size} · ${puzzle.size >= 25 ? "Epic" : "Grand"}`
        : puzzle.difficulty;
  // The heading names the page, not its outcome. It used to become the picture's name
  // on completion, which meant the h1 a search engine saw depended on whether the
  // grid happened to be finished — and it disagreed with the h1 the server rendered.
  // The reveal is worth keeping, so it moved to a line of its own below.
  const heading = pageHeading
    ?? (mode === "daily"
      ? dailyHeading
      : `${puzzle.difficulty[0]?.toUpperCase()}${puzzle.difficulty.slice(1)} Nonogram puzzle`);

  function handleZoomChange(next: number): void {
    setZoom(next);
    saveBoardZoom(puzzle.size, next);
  }

  async function shareResult(): Promise<void> {
    const text = resultText(puzzle, game.elapsedSeconds, game.mistakes, game.stats.dailyStreak, dailyDate);
    // A daily result names a date, so it links to that date's own page. The front page
    // moves on at midnight UTC, and a friend who opens the link tomorrow should get the
    // grid the result describes, not whatever is on the front page by then.
    const url = dailyDate ? new URL(dailyDatePath(dailyDate), window.location.origin).href : window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Nonogram Now", text, url });
        setShareStatus("Result shared.");
        track("share_clicked", { mode, share_target: "native" });
      } else {
        await copyText(`${text}${url}`);
        setShareStatus("Result copied.");
        track("share_clicked", { mode, share_target: "clipboard" });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setShareStatus("We couldn't share the result. Please try again.");
    }
  }

  return (
    <section
      className="puzzle-experience"
      aria-labelledby="puzzle-heading"
      data-solved={game.completed ? "true" : undefined}
    >
      {/* The brief leads the document but sits beside the board on wide screens, so the
          grid starts at the top of the page instead of below a stack of headings. */}
      <header className="puzzle-brief">
        <div className="brief-title">
          <p className="eyebrow">{eyebrow}</p>
          <h1 id="puzzle-heading">{heading}</h1>
          <TrustBadge />
          {game.completed && (
            <p className="brief-reveal">{puzzle.emoji} {puzzle.title}</p>
          )}
        </div>

        <dl className="game-readout" aria-label="Game status">
          <div>
            <dt>Time</dt>
            <dd>{formatTime(game.elapsedSeconds)}</dd>
          </div>
          <div className={game.mistakeCells.length === 0 ? undefined : "is-alert"}>
            <dt>Mistakes</dt>
            <dd>{game.mistakes}</dd>
          </div>
          {/* What kind of thinking the grid asks for, not how well it is going: the
              clock and the mistakes say the player, this row says the puzzle. */}
          <div className="logic-readout">
            <dt>Logic</dt>
            <dd title={SOLVE_TIERS[puzzle.solveTier].description}>{SOLVE_TIERS[puzzle.solveTier].label}</dd>
          </div>
          {mode === "daily" && (
            <div className="streak-readout">
              <dt>Streak</dt>
              <dd>{game.stats.dailyStreak > 0 ? `🔥 ${game.stats.dailyStreak}` : "0"}</dd>
            </div>
          )}
        </dl>

        <p className="puzzle-instruction" id="puzzle-instructions">
          Clue numbers count consecutive filled squares. Tap or drag to fill, right-click or long-press to cross.{" "}
          <a href="/how-to-play-nonogram">Full rules</a>
        </p>

        {mode === "daily" && !game.completed && (
          <p className="calendar-strip-link">
            <a href="/daily-nonogram/archive">Archive calendar</a>
          </p>
        )}

        {game.completed && (
          <div className="completion" role="status" aria-live="polite">
            <p>
              You solved it in {formatTime(game.elapsedSeconds)} with {game.mistakes}{" "}
              {game.mistakes === 1 ? "mistake" : "mistakes"}.
            </p>
            {mode === "daily" && game.stats.dailyStreak > 0 && (
              <p className="completion-streak">🔥 {game.stats.dailyStreak}-day streak — come back tomorrow.</p>
            )}
            {/* The name has just been revealed above; this is where it becomes a thing
                kept rather than a thing seen once — except in a sandbox, where nothing
                was kept and saying otherwise would be a plain untruth. */}
            <p className="completion-collected">
              {sandbox
                ? `${puzzle.emoji} ${puzzle.title} — playtest only, nothing recorded.`
                : <>{puzzle.emoji} {puzzle.title} added to your <a href="/collection">collection</a>.</>}
            </p>
            <div className="completion-actions">
              <button className="primary-button" type="button" onClick={() => void shareResult()}>
                <ShareAndroid width={18} height={18} strokeWidth={2} aria-hidden="true" /> Share result
              </button>
              {onNewPuzzle && (
                <button className="secondary-button" type="button" onClick={onNewPuzzle}>
                  <Play width={18} height={18} strokeWidth={2} aria-hidden="true" /> Play another
                </button>
              )}
            </div>
            {shareStatus && <p className="share-status">{shareStatus}</p>}
            {nextPuzzleAt && <p className="next-puzzle">A new daily puzzle arrives at midnight UTC.</p>}
          </div>
        )}
      </header>

      {/* Tools, board and footnotes share one surface so they read as a single object. */}
      <div className="board-panel">
        <GameControls
          tool={game.tool}
          playMode={game.playMode}
          boardMode={game.boardMode}
          canUndo={game.historyCount > 0}
          canRedo={game.redoCount > 0}
          completed={game.completed}
          zoom={zoom}
          assists={game.assists}
          onToolChange={game.selectTool}
          onPlayModeChange={game.setPlayMode}
          onBoardModeChange={game.setBoardMode}
          onUndo={game.undo}
          onRedo={game.redo}
          onCheck={game.checkBoard}
          onZoomChange={handleZoomChange}
          onAssistsChange={game.setAssists}
        />

        <GameBoard
          puzzle={puzzle}
          cells={game.cells}
          tool={game.tool}
          boardMode={game.boardMode}
          completed={game.completed}
          mistakeCells={game.mistakeCells}
          zoom={zoom}
          onPannableChange={setPannable}
          beginStroke={game.beginStroke}
          paintCell={game.paintCell}
          endStroke={game.endStroke}
          makeMove={game.makeMove}
          cycleMark={game.cycleMark}
          tapCycles={game.assists.tapCycles}
        />

        {/* The quiet strip under the grid: what just happened, and the one control
            that undoes the whole game. */}
        <div className="board-footnote">
          <p className={mistakeMessage ? "board-status is-mistake" : "board-status"} role="status" aria-live="polite">
            {statusMessage}
          </p>
          {/* One line, one message: the pan hint takes the strip only when there is
              no status to report, so a board that is wider than the screen never
              costs a row of its own. */}
          {!game.completed && !statusMessage && (
            panHint
              ? <p className="board-hint">{panHint}</p>
              : <p className="keyboard-hint">Arrows move · F fills · X crosses · M pans · ⌘Z undo</p>
          )}
          <ResetButton disabled={game.completed} onReset={game.reset} />
        </div>
      </div>

      <nav className="puzzle-more" aria-label="More puzzles">
        <p>More puzzles</p>
        {DIFFICULTY_NAV.map((item) => (
          <a
            key={item.href}
            href={item.href}
            aria-current={item.href === currentPath ? "true" : undefined}
          >
            {item.label} <small>{item.size}</small>
          </a>
        ))}
      </nav>
    </section>
  );
}
